import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { METODO_CONSUMO_LABEL, StatusVeiculo, TIPO_COMBUSTIVEL_LABEL, TIPO_VEICULO_LABEL } from '../../lib/enums'
import { cn, formatDate, formatMoney, formatPercent } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import type { RegistrarOdometroResultado, Veiculo } from '../../types'
import { CardConsumo, CardCusto, CardUltimo, CardVencimentos } from './CartoesVeiculo'
import { VeiculoFormModal } from './VeiculoFormModal'
import { energiaLabel, fonteFipe, formatKm, hojeIso, iconeVeiculo, mesReferencia, usaEnergiaEletrica } from './veiculoUtils'

export function VeiculoDetalhePage() {
  const { id = '' } = useParams()
  const modulo = useRecurso('veiculos.modulo')
  const fipeOk = useRecurso('veiculos.fipe').habilitado
  const abastecimentoOk = useRecurso('veiculos.abastecimento').habilitado
  const manutencaoOk = useRecurso('veiculos.manutencao').habilitado
  const insightsOk = useRecurso('veiculos.insights').habilitado
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState(false)
  const [atualizandoKm, setAtualizandoKm] = useState(false)
  const [vendendo, setVendendo] = useState(false)

  const { data: v, isLoading, isError, error } = useQuery({
    queryKey: ['veiculos', id], queryFn: () => veiculosApi.obter(id), enabled: !!id && modulo.habilitado,
  })

  const atualizarFipe = useMutation({
    mutationFn: () => veiculosApi.atualizarFipe(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['veiculos'] })
      if (r.aviso) toast.error(r.aviso); else toast.success('Valor FIPE atualizado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!modulo.habilitado) {
    return <><PageHeader title="Veículo" /><BloqueioRecurso mensagem="O módulo de veículos não está incluído no seu plano." /></>
  }
  if (isLoading) return <><PageHeader title="Veículo" /><Card><LoadingState /></Card></>
  if (isError || !v) return <><PageHeader title="Veículo" /><Card><ErrorState message={(error as Error)?.message || 'Veículo não encontrado.'} /></Card></>

  const vendido = v.status === StatusVeiculo.Vendido
  const podeAgir = v.podeEditar && !vendido
  const base = `/veiculos/${v.id}`

  return (
    <>
      <PageHeader title={v.apelido} />

      <Card className="mb-6">
        <CardBody className="flex flex-wrap items-center gap-4">
          <span className="inline-flex h-16 w-16 flex-shrink-0 items-center justify-center rounded bg-surface3 text-4xl text-white" style={v.corHex ? { color: v.corHex } : undefined}>
            <i className={iconeVeiculo(v.modelo.tipo, v.modelo.tipoEnergia)} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-xl font-semibold text-white">{v.apelido}</h3>
              {vendido && <Badge variant="warning">Vendido</Badge>}
              {v.compartilhado && <Badge variant="info" className="normal-case"><i className="ri-user-shared-line" aria-hidden /> de {v.donoNome}</Badge>}
            </div>
            <p className="text-13 text-muted">{v.modelo.marca} {v.modelo.modelo} {v.modelo.versao ?? ''} · {v.modelo.anoModelo}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge variant="muted">{TIPO_VEICULO_LABEL[v.modelo.tipo]}</Badge>
              <Badge variant="muted">{energiaLabel(v.modelo.tipoEnergia)}</Badge>
              {v.corNome && <Badge variant="muted" className="normal-case">{v.corNome}</Badge>}
              {v.placa && <Badge variant="muted">{v.placa}</Badge>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xs uppercase text-muted">Quilometragem</p>
            <p className="text-2xl font-semibold text-white">{formatKm(v.kmAtual)}</p>
          </div>
        </CardBody>
        <div className="flex flex-wrap items-center gap-2 border-t border-border/70 px-4 py-3">
          <AcaoRapida tutorial="garagem.abastecer" to={`${base}/abastecimentos`} icon="ri-gas-station-line" desativada={!podeAgir || !abastecimentoOk}>Abastecer</AcaoRapida>
          <Button variant="light" icon="ri-speed-up-line" disabled={!podeAgir} onClick={() => setAtualizandoKm(true)}>Atualizar km</Button>
          <AcaoRapida to={`${base}/manutencoes`} icon="ri-tools-line" desativada={!podeAgir}>Manutenção</AcaoRapida>
          <AcaoRapida to={`${base}/despesas`} icon="ri-money-dollar-circle-line" desativada={!podeAgir}>Despesa</AcaoRapida>
          <div className="ml-auto flex gap-2">
            {v.podeEditar && <Button variant="soft-primary" icon="ri-edit-line" onClick={() => setEditando(true)}>Editar</Button>}
            {podeAgir && <Button variant="soft-danger" icon="ri-price-tag-3-line" onClick={() => setVendendo(true)}>Marcar como vendido</Button>}
          </div>
        </div>
      </Card>

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Dados do veículo" icon="ri-information-line" />
          <CardBody>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-13">
              <Dado rotulo="Km inicial" valor={formatKm(v.odometroInicial)} />
              <Dado rotulo="Padrão de abastecimento" valor={METODO_CONSUMO_LABEL[v.metodoPadrao]} />
              <Dado rotulo="Combustíveis" valor={v.modelo.combustiveisAceitos.map((c) => TIPO_COMBUSTIVEL_LABEL[c]).join(', ') || '—'} />
              <Dado rotulo="Tanque / bateria" valor={[v.modelo.capacidadeTanqueL && `${v.modelo.capacidadeTanqueL} L`, v.modelo.capacidadeBateriaKwh && `${v.modelo.capacidadeBateriaKwh} kWh`].filter(Boolean).join(' · ') || '—'} />
              <Dado rotulo="Compra" valor={v.dataCompra ? formatDate(v.dataCompra) : '—'} />
              <Dado rotulo="Valor de compra" valor={v.valorCompra != null ? formatMoney(v.valorCompra) : '—'} />
              {usaEnergiaEletrica(v.modelo.tipoEnergia) && <Dado rotulo="Tarifa de casa" valor={v.tarifaKwhCasa != null ? `${formatMoney(v.tarifaKwhCasa)}/kWh` : '—'} />}
              {vendido && <Dado rotulo="Venda" valor={`${v.dataVenda ? formatDate(v.dataVenda) : '—'}${v.valorVenda != null ? ` · ${formatMoney(v.valorVenda)}` : ''}`} />}
            </dl>
          </CardBody>
        </Card>

        {fipeOk && (
          <Card>
            <CardHeader
              title="Valor FIPE" icon="ri-price-tag-3-line"
              actions={v.podeEditar && !vendido && <Button size="sm" variant="light" icon="ri-refresh-line" loading={atualizarFipe.isPending} onClick={() => atualizarFipe.mutate()}>Atualizar valor</Button>}
            />
            <CardBody>{v.valorFipe ? <BlocoFipe v={v} /> : <p className="text-13 text-muted">Sem valor da tabela FIPE para este veículo (modelo manual ou ainda não consultado).</p>}</CardBody>
          </Card>
        )}

        {abastecimentoOk && <CardConsumo veiculoId={v.id} />}
        {abastecimentoOk && <CardUltimo veiculoId={v.id} base={base} />}
        <CardVencimentos veiculoId={v.id} base={base} />
        <CardCusto veiculoId={v.id} comCombustivel={abastecimentoOk} comManutencao={manutencaoOk} base={base} />
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-13">
        {manutencaoOk && <Link to={`${base}/manutencoes`} className="text-muted hover:text-primary"><i className="ri-tools-line" aria-hidden /> Manutenções</Link>}
        {insightsOk && <Link to={`${base}/insights`} className="text-muted hover:text-primary"><i className="ri-line-chart-line" aria-hidden /> Insights e custo total</Link>}
      </div>

      <p className="mt-4 text-xs text-muted"><Link to="/veiculos" className="hover:text-primary"><i className="ri-arrow-left-line" aria-hidden /> Voltar para a garagem</Link></p>

      <VeiculoFormModal open={editando} onClose={() => setEditando(false)} veiculo={v} />
      <KmModal open={atualizandoKm} onClose={() => setAtualizandoKm(false)} veiculo={v} />
      <VenderModal open={vendendo} onClose={() => setVendendo(false)} veiculo={v} />
    </>
  )
}

function AcaoRapida({ to, icon, desativada, tutorial, children }: { to: string; icon: string; desativada: boolean; tutorial?: string; children: ReactNode }) {
  const cls = 'inline-flex h-[38px] items-center justify-center gap-2 whitespace-nowrap rounded bg-surface2 px-4 text-13 font-medium text-white transition-colors'
  if (desativada) return <span className={cn(cls, 'cursor-not-allowed opacity-50')}><i className={icon} aria-hidden />{children}</span>
  return <Link to={to} data-tutorial={tutorial} className={cn(cls, 'hover:bg-surface3')}><i className={icon} aria-hidden />{children}</Link>
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return <div><dt className="text-2xs uppercase text-muted">{rotulo}</dt><dd className="mt-0.5 text-white">{valor}</dd></div>
}

function BlocoFipe({ v }: { v: Veiculo }) {
  const f = v.valorFipe!
  const d = v.depreciacao
  return (
    <div className="space-y-3">
      <div>
        <p className="text-2xl font-semibold text-primary">{formatMoney(f.valor)}</p>
        <p className="text-xs text-muted">Referência {mesReferencia(f.mesReferencia)}{f.desatualizado ? ' · desatualizado' : ''}</p>
      </div>
      {f.desatualizado && <p className="rounded bg-warning/10 px-3 py-2 text-xs text-warning">O valor guardado não é do mês atual. Use &quot;Atualizar valor&quot; para renovar.</p>}
      {d && (
        <div className="grid grid-cols-2 gap-3 border-t border-border/70 pt-3 text-13">
          <Dado rotulo="Valor de compra" valor={formatMoney(d.valorCompra)} />
          <div>
            <dt className="text-2xs uppercase text-muted">{d.valor >= 0 ? 'Depreciação' : 'Valorização'}</dt>
            <dd className={cn('mt-0.5 font-medium', d.valor > 0 ? 'text-expense' : d.valor < 0 ? 'text-income' : 'text-white')}>
              {formatMoney(Math.abs(d.valor))} <span className="text-xs">({formatPercent(-d.percentual)})</span>
            </dd>
          </div>
        </div>
      )}
      <p className="text-xs text-muted">{fonteFipe(f)}</p>
    </div>
  )
}

function KmModal({ open, onClose, veiculo }: { open: boolean; onClose: () => void; veiculo: Veiculo }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [km, setKm] = useState('')
  const [alerta, setAlerta] = useState<RegistrarOdometroResultado | null>(null)

  function fechar() { setKm(''); setAlerta(null); onClose() }

  const registrar = useMutation({
    mutationFn: (confirmar: boolean) => veiculosApi.registrarOdometro(veiculo.id, { odometro: Number(km.replace(',', '.')), confirmarRegressao: confirmar || undefined }),
    onSuccess: (r) => {
      if (!r.gravado && r.alerta) { setAlerta(r); return }
      qc.invalidateQueries({ queryKey: ['veiculos'] })
      toast.success('Quilometragem atualizada.')
      fechar()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const valido = km.trim() !== '' && Number(km.replace(',', '.')) >= 0
  return (
    <Modal
      open={open} onClose={fechar} size="sm" title="Atualizar km"
      footer={alerta
        ? <><Button variant="light" onClick={() => setAlerta(null)}>Corrigir</Button><Button loading={registrar.isPending} onClick={() => registrar.mutate(true)}>Gravar mesmo assim</Button></>
        : <><Button variant="light" onClick={fechar}>Cancelar</Button><Button icon="ri-save-line" disabled={!valido} loading={registrar.isPending} onClick={() => registrar.mutate(false)}>Salvar</Button></>}
    >
      <div className="space-y-3">
        <Input label={`Km atual (último: ${formatKm(veiculo.kmAtual)})`} type="number" inputMode="numeric" min={0} value={km} disabled={!!alerta} autoFocus
          onChange={(e) => setKm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && valido && !alerta) registrar.mutate(false) }} />
        {alerta && (
          <div className="rounded bg-warning/10 px-3 py-2.5 text-13 text-warning" role="alert">
            <i className="ri-error-warning-line" aria-hidden /> {alerta.mensagem || 'Esse km parece diferente do esperado.'}
            <p className="mt-1 text-xs">Nada foi salvo ainda. Confirme só se o valor estiver certo.</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

function VenderModal({ open, onClose, veiculo }: { open: boolean; onClose: () => void; veiculo: Veiculo }) {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [data, setData] = useState(hojeIso())
  const [valor, setValor] = useState('')

  const vender = useMutation({
    mutationFn: () => veiculosApi.vender(veiculo.id, { data, valor: valor.trim() === '' ? null : Number(valor.replace(',', '.')) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['veiculos'] })
      toast.success('Veículo marcado como vendido.')
      onClose()
      navigate('/veiculos')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open={open} onClose={onClose} size="sm" title="Marcar como vendido"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button variant="danger" icon="ri-check-line" disabled={!data} loading={vender.isPending} onClick={() => vender.mutate()}>Sim, marcar como vendido</Button></>}
    >
      <div className="space-y-3">
        <p className="text-13 text-muted">
          <span className="text-white">{veiculo.apelido}</span> vai para a lista de vendidos e deixa de contar no limite do seu plano. O histórico é mantido.
        </p>
        <Input label="Data da venda" type="date" max={hojeIso()} value={data} onChange={(e) => setData(e.target.value)} />
        <Input label="Valor da venda (R$, opcional)" type="number" inputMode="decimal" min={0} step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} />
      </div>
    </Modal>
  )
}
